import React, { useEffect, useState, useRef } from 'react';
import './details-container.scss';
import ContentControls from '../../components/content-controls/content-controls';
import Paginator from '../../components/pagination/pagination';
import Validation from 'constants/validation-messages';
import { useSelector, useDispatch } from 'react-redux';
import { detailsDownloadRequest, getAccountsRequest, getAccountsReset } from '../../actions/accountActions';
import { DataTable, TableColumn } from '@ampol/design-system';
import { currencyFormat, getAccountLevelName } from '../../utilities/core-utilities';
import Status from '../../components/status/status';
import { getVehicleTypesAction } from 'actions/storeActions';
import { trackCustomEvent } from 'utilities/app-insights';
import { UserState } from 'types/UserState';

const DetailsContainer = ({ onItemSelected }) => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);

  const userStore: UserState = useSelector((state: any) => state.userStore);

  const assignedAccountNumber = userStore.assignedAccountNumber;
  const assignedGrandfatherClientNumber = userStore.assignedGrandfatherClientNumber;
  const detailResults = useSelector((state: any) => state.accountsStore.accounts);
  const detailResultsSize = useSelector((state: any) => state.accountsStore.totalSize);
  const detailResultsError = useSelector((state: any) => state.accountsStore.isError);
  const isAccountsLoading = useSelector((state: any) => state.accountsStore.isLoading);
  const vehicleTypes = useSelector((state: any) => state.getVehicleTypes.vehicleTypes);
  const loggedInUser = userStore.loggedInUser;

  const defaultFilterValues = {
    pageNumber: 1,
    pageSize: 10,
    sortTypeSelected: 'AccountNumber',
    sortDirection: false,
    searchTerm: '',
  };

  const [filterValues, _setFilterValues] = useState(defaultFilterValues);

  const setFilterValues = (values) => {
    if (JSON.stringify(values) === JSON.stringify(filterValues)) return;
    _setFilterValues(values);
  };

  useEffect(() => {
    window.dataLayer.push({
      event: 'Card_Portal_Selected_Account',
      B2CGUID: loggedInUser.userId,
      account_number: assignedAccountNumber,
    });
    dispatch(getAccountsReset());

    // Calls the get vehicle types request for Account Favourites
    if (vehicleTypes.length === 0) {
      dispatch(getVehicleTypesAction());
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [filterValues, assignedAccountNumber]);

  useEffect(() => {
    if (assignedAccountNumber && isMounted.current) {
      setFilterValues({
        ...defaultFilterValues,
      });
    }

    isMounted.current = true;
  }, [assignedAccountNumber]);

  const loadAccounts = () => {
    dispatch(
      getAccountsRequest(
        assignedAccountNumber,
        assignedGrandfatherClientNumber,
        filterValues.pageSize,
        filterValues.pageNumber,
        filterValues.sortTypeSelected,
        filterValues.sortDirection,
        filterValues.searchTerm
      )
    );
  };

  const searchAccounts = (query: string) => {
    trackCustomEvent('Search for Account', loggedInUser, assignedAccountNumber, 'Searched for Account Name or Number.');
    const searchValue = query?.toLowerCase();
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Search',
      search_input: searchValue,
    });

    setFilterValues({
      ...filterValues,
      searchTerm: searchValue,
      pageNumber: 1,
    });
  };

  const sortDetails = (column: string, isAsc: boolean) => {
    setFilterValues({
      ...filterValues,
      sortTypeSelected: column,
      sortDirection: isAsc,
      pageNumber: 1,
    });
  };

  const handlePageChange = (pageNumber: number) => {
    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of accounts data.'
    );
    setFilterValues({
      ...filterValues,
      pageNumber,
    });
  };

  const handleDownload = () => {
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Download',
      button_click: true,
    });

    trackCustomEvent(
      'Click Download',
      loggedInUser,
      assignedAccountNumber,
      'Click Download to download list of accounts.'
    );
    dispatch(detailsDownloadRequest(assignedGrandfatherClientNumber, assignedAccountNumber, filterValues.searchTerm));
  };

  const getAccountColumns = (): TableColumn[] => {
    return [
      {
        header: 'Account number',
        accessorKey: 'contractNumber',
        id: 'AccountNumber',
        defaultSorted: 'desc',
      },
      {
        header: 'Status',
        accessorFn: (status) => <Status value={status?.status} activeText='Account OK' />,
        id: 'Status',
      },
      {
        header: 'Account name',
        accessorKey: 'contractName',
        id: 'AccountName',
      },
      typeof detailResultsSize !== 'undefined' &&
        detailResultsSize > 1 && {
          header: 'Level',
          id: 'AccountLevel',
          accessorFn: (item) => getAccountLevelName(item.level),
          disableSort: true,
        },
      {
        header: "Today's balance",
        id: 'TodayBalance',
        accessorFn: (item) => currencyFormat(item.balance),
        rtl: true,
      },
      {
        header: 'Credit limit',
        id: 'CreditLimit',
        accessorFn: (item) => currencyFormat(item.creditLimit),
        rtl: true,
      },
      {
        header: 'Available Credit',
        id: 'AvailableCredit',
        accessorFn: (item) => currencyFormat(item.available),
        rtl: true,
      },
    ].filter(Boolean) as TableColumn[];
  };

  const rowClassCondition = (row: any) => {
    return row.level === 'A' ? 'account-table__row-grand-parent' : row.level === 'B' ? 'account-table__row-parent' : '';
  };

  return (
    <>
      <ContentControls
        showPageControls={true}
        showSearch={true}
        handleSearch={searchAccounts}
        handleDownload={handleDownload}
        selectControl={false}
        pageNumber={filterValues.pageNumber}
        itemCount={detailResultsSize}
        onPageChange={handlePageChange}
        defaultSearchValue={filterValues.searchTerm}
        totalRecords={detailResultsSize}
      />
      <div className='account-table'>
        <DataTable
          caption='Accounts'
          columns={getAccountColumns()}
          rowClassFn={(row) => rowClassCondition(row)}
          data={detailResults}
          isLoading={isAccountsLoading}
          tableVariant={'list'}
          onColumnSorted={sortDetails}
          onRowClick={onItemSelected}
          noDataMessage={detailResultsError ? Validation.CONTENT_ERROR : Validation.NO_CONTENT}
        />
      </div>

      <div className='mobile-paginator'>
        <Paginator
          itemCount={detailResultsSize}
          pageNumber={filterValues.pageNumber}
          onPageChange={handlePageChange}
          name={'details-container'}
        />
      </div>
    </>
  );
};

export default DetailsContainer;
